import { Grid } from '@material-ui/core';
import { useDeepEqualMemo } from '@superdispatch/hooks';
import { ColorDynamic, Stack } from '@superdispatch/ui';
import { Button } from '@superdispatch/ui-lab';
import { PostingSavedSearchDTO } from '../../data/dto/PostingSavedSearchDTO';
import { merge } from 'es-toolkit/compat';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDocumentTitle } from 'shared/layout/DocumentTitle';
import { WebviewLayout } from 'shared/layout/WebviewLayout';
import { isEmptySearchCriteria } from 'shared/modules/loadboard/PostingSearchCriteriaDTO';
import { useLocationParamsLegacy } from 'shared/routing/LocationParamsLegacy';
import { StickyAction } from 'shared/ui/StickyAction';
import styled from 'styled-components';
import { trackLoadboardEvent } from '../../core/LoadboardAnalytics';
import {
  AdvancedSearchForm,
  AdvancedSearchFormMode,
  useAdvancedSearchForm,
} from '../../core/saved-searches/AdvancedSearchForm';
import {
  createExternalCriteria,
  postingsPageParamsSchema,
} from '../../data/dto/PostingsPageParamsDTO';
import { useLoadboardContext } from '../../data/LoadboardContext';
import { markSearchAsUserTriggered } from '../../data/LoadboardPostingAPI';
import { useSavedSearch } from '../../data/LoadboardSavedSearchesAPI';

const Styled1 = styled.div`
  padding: 16px;
  background-color: ${ColorDynamic.White};
`;

export function AdvancedSearchPage() {
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [params, setParams] = useLocationParamsLegacy({
    yupSchema: postingsPageParamsSchema,
  });
  const { router } = useLoadboardContext();
  const {
    load_alert,
    current_saved_search_guid,
    search_criteria,
    current_tab,
  } = params;
  const { data: savedSearch } = useSavedSearch(current_saved_search_guid);

  const [mode, setMode] = useState<AdvancedSearchFormMode>(() =>
    load_alert === 'create'
      ? 'create'
      : current_saved_search_guid
        ? 'update'
        : 'search',
  );
  const defaultValues = useDeepEqualMemo(
    () => merge({}, savedSearch, { search_criteria }) as PostingSavedSearchDTO,
    [savedSearch, search_criteria],
  );
  const advancedSearchForm = useAdvancedSearchForm({
    mode,
    defaultValues,
    onValidationError() {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    },
    onSearch(nextCriteria) {
      router.openSearchPostingsScreen({
        strategy: 'replace',
        source: 'Advanced Search',
        searchCriteria: markSearchAsUserTriggered(nextCriteria.search_criteria),
      });
    },
    onSubmitSuccess(nextSavedSearch) {
      router.openSavedSearchScreen({
        replace: true,
        source: 'Load Alert',
        savedSearch: nextSavedSearch,
      });
    },
  });
  const { form, clearValues, isFormEmpty } = advancedSearchForm;
  const title = useMemo(
    () =>
      mode === 'update'
        ? 'Customize Load Alert'
        : mode === 'search'
          ? 'Search'
          : 'Create Load Alert',
    [mode],
  );

  useEffect(() => {
    const externalCriteria = createExternalCriteria(params);
    if (!isEmptySearchCriteria(externalCriteria)) {
      setParams(
        {
          pickup_state: undefined,
          pickup_city: undefined,
          delivery_city: undefined,
          delivery_state: undefined,
          search_criteria: externalCriteria,
        },
        { navigation: 'replace' },
      );
    }
  }, [params, setParams]);

  useEffect(() => {
    // Scroll to end to see description field
    if (mode === 'save-search' && contentRef.current) {
      contentRef.current.scrollTop = contentRef.current.scrollHeight;
    }
  }, [mode]);

  useDocumentTitle(title);

  return (
    <WebviewLayout contentRef={contentRef}>
      <Styled1>
        <Grid container={true} spacing={3}>
          <Grid item={true}>
            <AdvancedSearchForm
              {...advancedSearchForm}
              mode={mode}
              isPrivateLoadboard={current_tab === 'private-loadboard'}
              onAddToSavedSearch={() => {
                if (mode === 'search') {
                  setMode('save-search');
                } else {
                  setMode('search');
                }
              }}
            />
          </Grid>

          <Grid item={true} xs={12}>
            <Stack space="xsmall">
              {!isFormEmpty && (
                <Button
                  variant="text"
                  fullWidth={true}
                  disabled={form.isSubmitting}
                  onClick={() => {
                    clearValues();
                    setMode(mode === 'save-search' ? 'search' : mode);

                    trackLoadboardEvent({
                      name: 'SLB: UI Interaction',
                      type: 'FilterForm: Clicked Clear',
                    });
                  }}
                >
                  Clear All
                </Button>
              )}

              <StickyAction>
                <Button
                  fullWidth={true}
                  pending={form.isSubmitting}
                  onClick={() => {
                    form.handleSubmit();
                  }}
                >
                  {mode === 'update'
                    ? 'Update and Search'
                    : mode === 'search'
                      ? 'Search'
                      : 'Save and Search'}
                </Button>
              </StickyAction>
            </Stack>
          </Grid>
        </Grid>
      </Styled1>
    </WebviewLayout>
  );
}
